import React, { useState, useMemo, useCallback } from 'react';
import { useCart } from '../context/CartContext';
import { mediaUrl } from '../api';
import './MenuItem.css';

const NONE_SENTINEL = '__none__';

function isGenericOptionName(name) {
  return /^(yes|no|add|on|off)$/i.test(String(name || '').trim());
}

function optionIsSoldOut(group, opt) {
  return Boolean(
    group.track_stock
    && opt
    && opt.stock_quantity !== null
    && opt.stock_quantity !== undefined
    && opt.stock_quantity <= 0
  );
}

function singleOptionToggleLabel(group) {
  const opt = group.options?.[0];
  if (!opt) return group.name;
  if (isGenericOptionName(opt.name) || /^add /i.test(String(opt.name).trim())) {
    return group.name;
  }
  return opt.name;
}

// Parse the JSON-encoded option-id list a server stores in
// `modifier_groups.hidden_when_option_ids`. Returns an array of numbers
// (possibly empty). Defensive against null/empty/legacy/malformed values so
// any unexpected shape simply falls back to "no hide rule".
function parseHiddenWhenOptionIds(raw) {
  if (raw === null || raw === undefined || raw === '') return [];
  let arr = raw;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch { return []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => Number(x)).filter((x) => Number.isInteger(x) && x > 0);
}

export default function MenuItem({ item, disabled, disabledLabel }) {
  const { items, addItem, updateQuantity, cartKey } = useCart();
  const hasModifiers = item.modifier_groups && item.modifier_groups.length > 0;
  const [showDonation, setShowDonation] = useState(false);
  const [donationAmount, setDonationAmount] = useState(
    Number(item.price) > 0 ? String(item.price) : ''
  );
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  // Index into requiredGroups only (−1 = all required done / review).
  const [activeStep, setActiveStep] = useState(0);
  // Optional groups stay collapsed until the customer taps to customise.
  const [expandedOptional, setExpandedOptional] = useState({});

  // Build the set of currently-selected option ids across every group on this
  // item. A group is hidden when any id in its `hidden_when_option_ids` rule
  // matches one of these — e.g. picking "Long Black" or "Espresso" in a "Type"
  // group makes the "Milk" group disappear without affecting other coffees.
  const selectedOptionIds = useMemo(() => {
    if (!hasModifiers) return new Set();
    const ids = new Set();
    for (const g of item.modifier_groups) {
      const sel = selectedOptions[g.name];
      if (!sel || sel === NONE_SENTINEL) continue;
      const opt = g.options.find((o) => o.name === sel);
      if (opt) ids.add(opt.id);
    }
    return ids;
  }, [hasModifiers, item.modifier_groups, selectedOptions]);

  const isGroupHidden = useCallback((group) => {
    const triggers = parseHiddenWhenOptionIds(group.hidden_when_option_ids);
    if (triggers.length === 0) return false;
    return triggers.some((id) => selectedOptionIds.has(id));
  }, [selectedOptionIds]);

  const visibleModifierGroups = useMemo(
    () => (hasModifiers ? item.modifier_groups.filter((g) => !isGroupHidden(g)) : []),
    [hasModifiers, item.modifier_groups, isGroupHidden]
  );

  const soldOut = item.sold_out;
  const hasStockTrackedOptions = hasModifiers && item.modifier_groups.some((g) => g.track_stock);
  const stockLeft = !hasStockTrackedOptions && item.stock_quantity !== null && item.stock_quantity !== undefined
    ? item.stock_quantity
    : null;

  // Cart totals for this menu item (used to clamp the per-account daily cap).
  const cartItemsForThis = items.filter((i) => i.menu_item_id === item.id);
  const totalQty = cartItemsForThis.reduce((sum, i) => sum + i.quantity, 0);

  // Per-account daily cap. `your_used_today` is only populated by the server when
  // the customer is signed in; for guests it's null and we don't show or enforce
  // anything client-side (server-side enforcement only kicks in for logged-in users).
  const hasUserLimit = item.max_per_user !== null && item.max_per_user !== undefined
    && item.your_used_today !== null && item.your_used_today !== undefined;
  const userRemainingForToday = hasUserLimit
    ? Math.max(0, item.max_per_user - item.your_used_today - totalQty)
    : Infinity;

  // The stepper max for a single cart row is the tighter of stock and account cap
  // (account cap covers ALL rows of this item, so don't subtract this row's qty here).
  const stockCap = stockLeft !== null ? stockLeft : Infinity;
  const accountCap = hasUserLimit ? item.max_per_user - item.your_used_today : Infinity;
  const maxQty = Math.min(stockCap, accountCap);
  const atUserLimit = hasUserLimit && userRemainingForToday <= 0;

  // Surcharge, required-check and sold-out-check all walk only the *visible*
  // groups: a hidden group's previous selection (e.g. a milk picked before
  // the customer switched the coffee type to Long Black) must not affect
  // price or block "Add to Cart".
  const surcharge = useMemo(() => {
    if (!hasModifiers) return 0;
    let total = 0;
    for (const group of visibleModifierGroups) {
      const sel = selectedOptions[group.name];
      if (sel && sel !== NONE_SENTINEL) {
        const opt = group.options.find((o) => o.name === sel);
        if (opt) total += opt.surcharge;
      }
    }
    return total;
  }, [hasModifiers, visibleModifierGroups, selectedOptions]);

  const livePrice = item.price + surcharge;

  const requiredGroups = useMemo(
    () => visibleModifierGroups.filter((g) => g.required),
    [visibleModifierGroups]
  );
  const optionalGroups = useMemo(
    () => visibleModifierGroups.filter((g) => !g.required),
    [visibleModifierGroups]
  );

  const allRequiredSelected = requiredGroups.every(
    (g) => selectedOptions[g.name] && selectedOptions[g.name] !== NONE_SENTINEL
  );

  const allOptionsBlocked = hasModifiers && requiredGroups.some((g) =>
    g.track_stock && g.options.every((o) => o.stock_quantity !== null && o.stock_quantity !== undefined && o.stock_quantity <= 0)
  );

  const isStepCompleted = useCallback((group) => {
    const v = selectedOptions[group.name];
    return Boolean(v && v !== NONE_SENTINEL);
  }, [selectedOptions]);

  function buildOptionsLabel(opts) {
    return Object.values(opts).filter((v) => v && v !== NONE_SENTINEL).join(', ');
  }

  function resetModifierUi() {
    setShowOptions(false);
    setSelectedOptions({});
    setActiveStep(0);
    setExpandedOptional({});
  }

  function handleAddWithOptions() {
    const visibleNames = new Set(visibleModifierGroups.map((g) => g.name));
    const cleanOptions = {};
    for (const [k, v] of Object.entries(selectedOptions)) {
      // Skip selections that belong to a group that is currently hidden by a
      // conditional rule (e.g. picking Long Black after first picking Soy
      // milk must not send the milk option to the kitchen).
      if (!visibleNames.has(k)) continue;
      if (v && v !== NONE_SENTINEL) cleanOptions[k] = v;
    }
    addItem({
      id: item.id,
      name: item.name,
      price: livePrice,
      kitchen: item.kitchen,
      venue: item.venue,
      options: Object.keys(cleanOptions).length > 0 ? cleanOptions : null,
      optionsLabel: buildOptionsLabel(cleanOptions),
    });
    resetModifierUi();
  }

  function handleSimpleAdd() {
    if (item.variable_price) {
      setShowDonation(true);
      return;
    }
    if (hasModifiers) {
      setShowOptions(true);
      setActiveStep(0);
      setExpandedOptional({});
      return;
    }
    addItem({
      id: item.id,
      name: item.name,
      price: item.price,
      kitchen: item.kitchen,
      venue: item.venue,
    });
  }

  function handleDonationAdd() {
    const amount = Math.round(Number(donationAmount) * 100) / 100;
    if (!Number.isFinite(amount) || amount < 0.01) {
      window.alert('Enter a donation amount');
      return;
    }
    addItem({
      id: item.id,
      name: item.name,
      price: amount,
      kitchen: item.kitchen,
      venue: item.venue,
      variableAmount: amount,
    });
    setShowDonation(false);
  }

  function handleCancel() {
    resetModifierUi();
  }

  function handleSelectRequiredOption(group, value) {
    // Recompute visibility *with the new selection* so we can advance past any
    // group that just became hidden (e.g. picking Long Black collapses Milk).
    const nextSelections = { ...selectedOptions, [group.name]: value };
    const nextSelectedIds = new Set();
    for (const g of item.modifier_groups) {
      const sel = nextSelections[g.name];
      if (!sel || sel === NONE_SENTINEL) continue;
      const opt = g.options.find((o) => o.name === sel);
      if (opt) nextSelectedIds.add(opt.id);
    }
    const nextRequired = item.modifier_groups.filter((g) => {
      if (!g.required) return false;
      const triggers = parseHiddenWhenOptionIds(g.hidden_when_option_ids);
      return triggers.length === 0 || !triggers.some((id) => nextSelectedIds.has(id));
    });

    setSelectedOptions(nextSelections);

    const justIdx = nextRequired.findIndex((g) => g.id === group.id);
    const nextIdx = justIdx + 1;
    if (justIdx >= 0 && nextIdx < nextRequired.length) {
      setTimeout(() => setActiveStep(nextIdx), 200);
    } else {
      setTimeout(() => setActiveStep(-1), 200);
    }
  }

  function handleSelectOptionalOption(group, value) {
    setSelectedOptions((prev) => {
      if (prev[group.name] === value) {
        const next = { ...prev };
        delete next[group.name];
        return next;
      }
      return { ...prev, [group.name]: value };
    });
  }

  function handleToggleSingleOptional(group) {
    const opt = group.options?.[0];
    if (!opt || optionIsSoldOut(group, opt)) return;
    handleSelectOptionalOption(group, opt.name);
  }

  function clearOptionalGroup(group) {
    setSelectedOptions((prev) => {
      const next = { ...prev };
      delete next[group.name];
      return next;
    });
    setExpandedOptional((prev) => ({ ...prev, [group.id]: false }));
  }

  function toggleOptionalExpanded(group) {
    setExpandedOptional((prev) => ({ ...prev, [group.id]: !prev[group.id] }));
  }

  const simpleKey = cartKey(item.id, null);
  const simpleCartItem = !hasModifiers ? items.find((i) => i._key === simpleKey) : null;
  const simpleQty = simpleCartItem?.quantity || 0;

  return (
    <div className={`menu-item card ${soldOut ? 'menu-item-soldout' : ''}${disabled ? ' menu-item-disabled' : ''}`}>
      {item.image_url && (
        <img src={mediaUrl(item.image_url)} alt={item.name} className="menu-item-img" />
      )}
      <div className="menu-item-info">
        <div className="menu-item-name-row">
          <h3 className="menu-item-name">{item.name}</h3>
          {item.is_adhoc === 1 && <span className="menu-item-special">Special</span>}
        </div>
        {item.description && (
          <p className="menu-item-desc">{item.description}</p>
        )}
        <div className="menu-item-meta">
          <p className="menu-item-price">
            {item.variable_price
              ? 'Any amount'
              : `$${Number(item.price).toFixed(2)}`}
            {hasModifiers && !item.variable_price && <span className="menu-item-from">+</span>}
          </p>
          {soldOut && <span className="menu-item-soldout-badge">Sold Out</span>}
          {!soldOut && stockLeft !== null && stockLeft <= 5 && (
            <span className="menu-item-low-stock">{stockLeft} left</span>
          )}
          {!soldOut && hasUserLimit && (
            <span className="menu-item-user-limit">
              {item.your_used_today > 0
                ? `Limit ${item.max_per_user}/day · ${item.your_used_today} used`
                : `Limit ${item.max_per_user}/day`}
            </span>
          )}
        </div>
      </div>
      <div className="menu-item-actions">
        {disabled ? (
          <span className="menu-item-unavailable" style={{ fontSize: 12 }}>{disabledLabel || 'Unavailable'}</span>
        ) : soldOut ? (
          <span className="menu-item-unavailable">Unavailable</span>
        ) : atUserLimit ? (
          <span className="menu-item-unavailable">Daily limit reached</span>
        ) : item.variable_price ? (
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSimpleAdd}
          >
            Donate
          </button>
        ) : !hasModifiers ? (
          simpleQty === 0 ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSimpleAdd}
              disabled={maxQty <= 0}
            >
              Add
            </button>
          ) : (
            <div className="qty-control">
              <button
                className="qty-btn"
                onClick={() => updateQuantity(simpleKey, simpleQty - 1)}
              >
                &minus;
              </button>
              <span className="qty-value">{simpleQty}</span>
              <button
                className="qty-btn qty-btn-add"
                onClick={() => updateQuantity(simpleKey, Math.min(simpleQty + 1, maxQty))}
                disabled={simpleQty >= maxQty}
              >
                +
              </button>
            </div>
          )
        ) : (
          <div className="modifier-add-area">
            <button className="btn btn-primary btn-sm" onClick={() => { setShowOptions(true); setActiveStep(0); setExpandedOptional({}); }}>
              {totalQty > 0 ? 'Add More' : 'Add'}
            </button>
          </div>
        )}
      </div>

      {showDonation && (
        <div className="menu-item-donation">
          <label htmlFor={`donation-${item.id}`}>Amount ($)</label>
          <input
            id={`donation-${item.id}`}
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={donationAmount}
            onChange={(e) => setDonationAmount(e.target.value)}
          />
          <div className="menu-item-donation-quick">
            {[2, 5, 10, 20].map((n) => (
              <button key={n} type="button" className="btn btn-sm btn-outline" onClick={() => setDonationAmount(String(n))}>
                ${n}
              </button>
            ))}
          </div>
          <div className="menu-item-donation-actions">
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowDonation(false)}>Cancel</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleDonationAdd}>Add donation</button>
          </div>
        </div>
      )}

      {cartItemsForThis.length > 0 && Number(item.variable_price) === 1 && !showDonation && (
        <div className="modifier-cart-variants">
          {cartItemsForThis.map((ci) => (
            <div key={ci._key} className="modifier-variant-row">
              <span className="modifier-variant-label">${Number(ci.price).toFixed(2)}</span>
              <div className="qty-control qty-control-sm">
                <button className="qty-btn" onClick={() => updateQuantity(ci._key, ci.quantity - 1)}>&minus;</button>
                <span className="qty-value">{ci.quantity}</span>
                <button className="qty-btn qty-btn-add" onClick={() => updateQuantity(ci._key, ci.quantity + 1)}>+</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {cartItemsForThis.length > 0 && hasModifiers && !showOptions && (
        <div className="modifier-cart-variants">
          {cartItemsForThis.map((ci) => (
            <div key={ci._key} className="modifier-variant-row">
              <span className="modifier-variant-label">
                {ci.optionsLabel || 'No options'}
              </span>
              <div className="qty-control qty-control-sm">
                <button
                  className="qty-btn"
                  onClick={() => updateQuantity(ci._key, ci.quantity - 1)}
                >
                  &minus;
                </button>
                <span className="qty-value">{ci.quantity}</span>
                <button
                  className="qty-btn qty-btn-add"
                  onClick={() => updateQuantity(ci._key, ci.quantity + 1)}
                  disabled={atUserLimit}
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showOptions && hasModifiers && (
        <div className="modifier-panel">
          <div className="modifier-steps">
            {requiredGroups.map((group, idx) => {
              const isActive = idx === activeStep;
              const completed = isStepCompleted(group);
              const selectedValue = selectedOptions[group.name];
              const selectedOpt = selectedValue
                ? group.options.find((o) => o.name === selectedValue)
                : null;

              return (
                <div
                  key={group.id}
                  className={`modifier-step ${isActive ? 'modifier-step-active' : ''} ${completed && !isActive ? 'modifier-step-completed' : ''}`}
                >
                  <div
                    className="modifier-step-header"
                    onClick={() => setActiveStep(idx)}
                  >
                    <div className="modifier-step-indicator">
                      {completed && !isActive ? (
                        <span className="modifier-step-check">&#10003;</span>
                      ) : (
                        <span className="modifier-step-number">{idx + 1}</span>
                      )}
                    </div>
                    <div className="modifier-step-title">
                      <span className="modifier-group-label">
                        {group.name}
                        <span className="modifier-required-star"> *</span>
                      </span>
                      {completed && !isActive && (
                        <span className="modifier-step-selection">
                          {selectedValue}
                          {selectedOpt && selectedOpt.surcharge > 0 ? ` (+$${selectedOpt.surcharge.toFixed(2)})` : ''}
                        </span>
                      )}
                    </div>
                    {completed && !isActive && (
                      <button
                        type="button"
                        className="modifier-step-change"
                        onClick={(e) => { e.stopPropagation(); setActiveStep(idx); }}
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {isActive && (
                    <div className="modifier-step-body">
                      <div className="modifier-option-list">
                        {group.options.map((opt) => {
                          const optSoldOut = group.track_stock && opt.stock_quantity !== null && opt.stock_quantity !== undefined && opt.stock_quantity <= 0;
                          const isSelected = selectedValue === opt.name;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              className={`modifier-option-btn ${isSelected ? 'modifier-option-selected' : ''} ${optSoldOut ? 'modifier-option-soldout' : ''}`}
                              disabled={optSoldOut}
                              onClick={() => handleSelectRequiredOption(group, opt.name)}
                            >
                              <span className="modifier-option-name">{opt.name}</span>
                              {opt.surcharge > 0 ? (
                                <span className="modifier-option-surcharge">+${opt.surcharge.toFixed(2)}</span>
                              ) : null}
                              {optSoldOut ? <span className="modifier-option-soldout-tag">Sold Out</span> : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {optionalGroups.length > 0 && (
            <div className="modifier-optional-section">
              <div className="modifier-optional-heading">Optional extras</div>
              {optionalGroups.map((group) => {
                const selectedValue = selectedOptions[group.name];
                const selectedOpt = selectedValue
                  ? group.options.find((o) => o.name === selectedValue)
                  : null;
                const single = group.options.length === 1;
                if (single) {
                  const opt = group.options[0];
                  const selected = selectedValue === opt.name;
                  const label = singleOptionToggleLabel(group);
                  const soldOut = optionIsSoldOut(group, opt);
                  return (
                    <div key={group.id} className="modifier-optional-row">
                      <button
                        type="button"
                        className={`modifier-optional-toggle modifier-optional-one-tap${selected ? ' is-on' : ''}`}
                        disabled={soldOut}
                        onClick={() => handleToggleSingleOptional(group)}
                        aria-pressed={selected}
                      >
                        <span className="modifier-optional-toggle-label">
                          {selected ? label : `+ ${label}`}
                        </span>
                        <span className="modifier-optional-toggle-hint">
                          {soldOut
                            ? 'Sold out'
                            : opt.surcharge > 0
                              ? `+$${Number(opt.surcharge).toFixed(2)}`
                              : selected
                                ? 'Added — tap to remove'
                                : 'Tap to add'}
                        </span>
                      </button>
                    </div>
                  );
                }
                const expanded = Boolean(expandedOptional[group.id]) || Boolean(selectedValue);
                return (
                  <div
                    key={group.id}
                    className={`modifier-optional-row ${expanded ? 'modifier-optional-expanded' : ''}`}
                  >
                    <button
                      type="button"
                      className="modifier-optional-toggle"
                      onClick={() => toggleOptionalExpanded(group)}
                    >
                      <span className="modifier-optional-toggle-label">
                        {selectedValue ? group.name : `+ ${group.name}`}
                      </span>
                      {selectedValue ? (
                        <span className="modifier-optional-toggle-value">
                          {selectedValue}
                          {selectedOpt && selectedOpt.surcharge > 0
                            ? ` (+$${selectedOpt.surcharge.toFixed(2)})`
                            : ''}
                        </span>
                      ) : (
                        <span className="modifier-optional-toggle-hint">Tap only if needed</span>
                      )}
                    </button>
                    {expanded && (
                      <div className="modifier-step-body">
                        <div className="modifier-option-list">
                          {group.options.map((opt) => {
                            const optSoldOut = optionIsSoldOut(group, opt);
                            const isSelected = selectedValue === opt.name;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                className={`modifier-option-btn ${isSelected ? 'modifier-option-selected' : ''} ${optSoldOut ? 'modifier-option-soldout' : ''}`}
                                disabled={optSoldOut}
                                onClick={() => handleSelectOptionalOption(group, opt.name)}
                              >
                                <span className="modifier-option-name">{opt.name}</span>
                                {opt.surcharge > 0 ? (
                                  <span className="modifier-option-surcharge">+${opt.surcharge.toFixed(2)}</span>
                                ) : null}
                                {optSoldOut ? <span className="modifier-option-soldout-tag">Sold Out</span> : null}
                              </button>
                            );
                          })}
                          {selectedValue && (
                            <button
                              type="button"
                              className="modifier-option-btn modifier-option-clear"
                              onClick={() => clearOptionalGroup(group)}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="modifier-panel-footer">
            <span className="modifier-live-price">${livePrice.toFixed(2)}</span>
            <div className="modifier-panel-actions">
              <button
                className="btn btn-primary btn-sm"
                disabled={!allRequiredSelected || allOptionsBlocked || atUserLimit}
                onClick={handleAddWithOptions}
              >
                {allOptionsBlocked
                  ? 'All Options Sold Out'
                  : atUserLimit
                    ? 'Daily Limit Reached'
                    : 'Add to Cart'}
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
